import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams as useReactRouterParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { UserAuthProvider, useUserAuth } from "@/contexts/UserAuthContext";
import { PaywallProvider } from "@/contexts/PaywallContext";
import { UploadManagerProvider } from "@/contexts/UploadManagerContext";
import { ChannelProvider, useChannel } from "@/contexts/ChannelContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminOnlyRoute from "@/components/AdminOnlyRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import ScrollToTop from "@/components/ScrollToTop";
import BackButton from "@/components/BackButton";
import SEOHead from "@/components/SEOHead";
import usePageTracking from "@/hooks/usePageTracking";
import { useMessagesBackTracker } from "@/hooks/useMessagesBackTarget";
import Navbar from "@/components/Navbar";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import Footer from "@/components/Footer";
import CookieConsentBanner from "@/components/CookieConsentBanner";
import { AppStorePromoPopup } from "@/components/AppStorePromo";
import MetaRouteAnalytics from "@/components/MetaRouteAnalytics";
import BottomNav from "@/components/BottomNav";
import GlobalUploadBar from "@/components/GlobalUploadBar";
import ReviewerUpgradeCTA from "@/components/reviewer/ReviewerUpgradeCTA";
import ReviewerQuickAccessPanel from "@/components/reviewer/ReviewerQuickAccessPanel";
import ReviewerGuideBanner from "@/components/reviewer/ReviewerGuideBanner";
const SwipeNavigator = lazy(() => import("@/components/SwipeNavigator"));
import LoginModal from "@/components/LoginModal";
import ClaimCodeSheet from "@/components/ClaimCodeSheet";
import ReferralCodeCatcher from "@/components/ambassador/ReferralCodeCatcher";
import DobGuard from "@/components/DobGuard";
import WelcomeSpotlightTour from "@/components/onboarding/WelcomeSpotlightTour";
import { installHealthReporter } from "@/lib/healthReporter";
import { readFlytIdFromHost, getVanityApexSlug, isNativeApp } from "@/lib/nativeBridge";

// iter307 · Fire the global 5xx axios interceptor once at module load.
installHealthReporter();
import ApplyToBeFeaturedModal from "@/components/ApplyToBeFeaturedModal";
import PersonaWelcomeModal from "@/components/PersonaWelcomeModal";
import WelcomeGate from "@/components/WelcomeGate";
import { Toaster } from "@/components/ui/sonner";
import AuthCallback from "@/components/AuthCallback";
import GoogleAuthCallback from "@/components/GoogleAuthCallback";
import Homepage from "@/pages/Homepage";
// NetworkPage is the first visible block on `/` (the new Network home),
// so we eager-import it too. Keeping it lazy was triggering a Suspense
// fallback flash on the LCP route, which hurt Largest-Contentful-Paint.
import NetworkPage from "@/pages/NetworkPage";
const TeamAthletesPage = lazy(() => import("@/pages/TeamAthletesPage"));
// AthleteNetworkPage is the single most-tapped destination from `/` —
// every athlete card on the network grid lands here. Lazy-loading it
// caused a brief white-flash on the suspended chunk download. Eager
// keeps the dark surface continuous and the navigation feels instant.
import AthleteNetworkPage from "@/pages/AthleteNetworkPage";
// LaunchSplash eager-imported so the black cold-open paints on the very
// first frame, before any lazy chunks arrive. If we lazy-loaded it the user
// would see the homepage flash for ~50ms before the splash mounted on top.
import LaunchSplash from "@/components/LaunchSplash";
import PullToRefresh from "@/components/PullToRefresh";
// Eager — used as the Suspense fallback for every lazy route + as the
// initial loading state on data-fetching pages. Keeping it eager avoids
// a chicken-and-egg where the fallback itself needs to be lazily
// downloaded before it can paint.
import PageSkeleton from "@/components/PageSkeleton";
// All non-LCP routes are lazy — they only hydrate when the user navigates to them.
// This pulls hundreds of KB out of the initial JS bundle.
const StoriesIndex = lazy(() => import("@/pages/StoriesIndex"));
const PayoutsReturnPage = lazy(() => import("@/pages/PayoutsReturnPage"));
const CollectionsIndexPage = lazy(() => import("@/pages/CollectionsIndexPage"));
const CollectionPage = lazy(() => import("@/pages/CollectionPage"));
// iter158 · /access was a "Coming Soon" waitlist that didn't fit the
// current athlete-signup flow.  Retired the page; any legacy link
// (marketing, guides, homepage banners) now redirects to /for-athletes
// which is our real conversion surface.
const TheNetworkPage = lazy(() => import("@/pages/TheNetworkPage"));
const StoryPage = lazy(() => import("@/pages/StoryPage"));

// Lazy-loaded routes — split out of the initial bundle for faster first paint.
// Heavy pages (Admin uses recharts, StoryEditor, etc.) load only when visited.
const AthletesPage = lazy(() => import("@/pages/AthletesPage"));
const DiscoverFlytPage = lazy(() => import("@/pages/DiscoverFlytPage"));
const BrandsPage = lazy(() => import("@/pages/BrandsPage"));
const BrandNetworkPage = lazy(() => import("@/pages/BrandNetworkPage"));
const ClientPortalPage = lazy(() => import("@/pages/ClientPortalPage"));
const ClientMessagesPage = lazy(() => import("@/pages/ClientMessagesPage"));
const ProgramsPage = lazy(() => import("@/pages/ProgramsPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
// Payment routes removed pre-launch — Stripe is gone.
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const TeamLoginPage = lazy(() => import("@/pages/TeamLoginPage"));
const TeamConsolePage = lazy(() => import("@/pages/TeamConsolePage"));
// iter490 · Standalone /start ad-target landing page — see
// pages/onboarding/FlytIdOnboardingPage for the full 13-step flow.
const FlytIdOnboardingPage = lazy(() => import("@/pages/onboarding/FlytIdOnboardingPage"));
const ShareStatsPage = lazy(() => import("@/pages/ShareStatsPage"));
// iter666 · Founder-only athlete⇄account link diagnostics + repair.
const AthleteDoctorPage = lazy(() => import("@/pages/AthleteDoctorPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const FlytProcessPage = lazy(() => import("@/pages/FlytProcessPage"));
const FlytClientPage = lazy(() => import("@/pages/FlytClientPage"));
const FlytCaseStudiesPage = lazy(() => import("@/pages/FlytCaseStudiesPage"));
const SubmitAdPage = lazy(() => import("@/pages/SubmitAdPage"));
const ProposalPage = lazy(() => import("@/pages/ProposalPage"));
const NetworkLeaguePage = lazy(() => import("@/pages/NetworkLeaguePage"));
const ProfilePage = lazy(() => import("@/pages/ProfilePage"));
const NetworkProfilePage = lazy(() => import("@/pages/NetworkProfilePage"));
const ResetPasswordPage = lazy(() => import("@/pages/ResetPasswordPage"));
const BillingPage = lazy(() => import("@/pages/BillingPage"));
const UpgradePage = lazy(() => import("@/pages/UpgradePage"));
const SupportPage = lazy(() => import("@/pages/SupportPage"));
const PicksPublicPage = lazy(() => import("@/pages/PicksPublicPage"));
const PicksPortalPage = lazy(() => import("@/pages/PicksPortalPage"));
const ClaimAndSignupPage = lazy(() => import("@/pages/ClaimAndSignupPage"));
const PayoutsPage = lazy(() => import("@/pages/PayoutsPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const BlockedUsersPage = lazy(() => import("@/pages/BlockedUsersPage"));
const FounderModerationReportsPage = lazy(() => import("@/pages/FounderModerationReportsPage"));
const FounderSentryTestPage = lazy(() => import("@/pages/FounderSentryTestPage"));
const AnalyticsModalPreviewPage = lazy(() => import("@/pages/AnalyticsModalPreviewPage"));
const WeeklyInsightsCardPage = lazy(() => import("@/pages/WeeklyInsightsCardPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const FounderTrainingPage = lazy(() => import("@/pages/FounderTrainingPage"));
const FounderMatchPage = lazy(() => import("@/pages/FounderMatchPage"));
const CreatorAgreementPage = lazy(() => import("@/pages/CreatorAgreementPage"));
const EarnProgramTermsPage = lazy(() => import("@/pages/EarnProgramTermsPage"));
// iter302 · FLYT Ambassador Program
const AmbassadorDashboard = lazy(() => import("@/pages/AmbassadorDashboard"));
const AmbassadorRedirect = lazy(() => import("@/pages/AmbassadorRedirect"));
const AmbassadorSignupPage = lazy(() => import("@/pages/AmbassadorSignupPage"));
const TxHsDistrictPage = lazy(() => import("@/pages/TxHsDistrictPage"));
const GuardianConsentPortalPage = lazy(() => import("@/pages/GuardianConsentPortalPage"));
const PartnersPage = lazy(() => import("@/pages/PartnersPage"));
const AthleteMePage = lazy(() => import("@/pages/AthleteMePage"));
const AthleteCareerHomePage = lazy(() => import("@/pages/AthleteCareerHomePage"));
const AthleteHome = lazy(() => import("@/pages/AthleteHome"));
const AthleteCareerMediaPage = lazy(() => import("@/pages/AthleteCareerMediaPage"));
const _importAthleteSharePage = () => import("@/pages/AthleteSharePage");
const AthleteSharePage = lazy(_importAthleteSharePage);
// CareerCoachBubble now mounts inside ProfilePage.js (iter37) — no
// global lazy import needed.
const AgentDashboardPage = lazy(() => import("@/pages/AgentDashboardPage"));
const _importAgentLinkPublicPage = () => import("@/pages/AgentLinkPublicPage");
const AgentLinkPublicPage = lazy(_importAgentLinkPublicPage);
// Bio-link fast path: when the visitor lands directly on /l/* (Instagram
// bio taps), start downloading the route chunk immediately — in parallel
// with React bootstrapping — instead of waiting for the first render to
// hit the lazy() boundary. Removes one full network round-trip.
if (typeof window !== 'undefined') {
  const _lp = window.location.pathname;
  if (_lp.startsWith('/l/agent/')) _importAgentLinkPublicPage();
  else if (_lp.startsWith('/l/')) _importAthleteSharePage();
  // iter517 · Personalized-subdomain fast path (jaxon.flythouse.com).
  // Prefetch the same chunk whenever the wildcard host + a valid slug
  // are present in the browser's hostname. Falls through gracefully on
  // apex, reserved, or malformed subdomains.
  try {
    if (readFlytIdFromHost()) _importAthleteSharePage();
  } catch { /* nativeBridge not present in test env — ignore */ }
}
const BrandReachOutKitPage = lazy(() => import("@/pages/BrandReachOutKitPage"));
const AgentInviteAcceptPage = lazy(() => import("@/pages/AgentInviteAcceptPage"));
const AthleteInviteAcceptPage = lazy(() => import("@/pages/AthleteInviteAcceptPage"));
const RosterMessagesPage = lazy(() => import("@/pages/RosterMessagesPage"));
const AthleteMessagesPage = lazy(() => import("@/pages/AthleteMessagesPage"));
const BrandDashboardPage = lazy(() => import("@/pages/BrandDashboardPage"));
const AgentSignupPage = lazy(() => import("@/pages/AgentSignupPage"));
const BrandSignupPage = lazy(() => import("@/pages/BrandSignupPage"));
const RecruiterSignupPage = lazy(() => import("@/pages/RecruiterSignupPage"));
const RecruiterDashboardPage = lazy(() => import("@/pages/RecruiterDashboardPage"));
const RecruiterAthleteDetailPage = lazy(() => import("@/pages/RecruiterAthleteDetailPage"));
const WelcomePage = lazy(() => import("@/pages/WelcomePage"));
const FlytStudiosPage = lazy(() => import("@/pages/FlytStudiosPage"));
const SearchPage = lazy(() => import("@/pages/SearchPage"));
const PulsePage = lazy(() => import("@/pages/PulsePage"));
const SecretUnlockPage = lazy(() => import("@/pages/SecretUnlockPage"));
const SecretRevealPage = lazy(() => import("@/pages/SecretRevealPage"));
const SecretStoryPage = lazy(() => import("@/pages/SecretStoryPage"));
const AthleteDashboardPage = lazy(() => import("@/pages/AthleteDashboardPage"));
// iter54 · School OS (Athletic Dept operating system) — foundation phase
const StaffLoginPage = lazy(() => import("@/pages/staff/StaffLoginPage"));
const StaffAcceptInvitePage = lazy(() => import("@/pages/staff/StaffAcceptInvitePage"));
const SchoolDashboardPage = lazy(() => import("@/pages/staff/SchoolDashboardPage"));
// DemoMockupPage is a low-traffic, share-link-only surface (/demo,
// /demo/:token) — not on the LCP-critical home/network path, so unlike
// Homepage/NetworkPage/AthleteNetworkPage above it's safe to lazy-load.
const DemoMockupPage = lazy(() => import("@/pages/DemoMockupPage"));
const SchoolAthletesPage = lazy(() => import("@/pages/staff/SchoolAthletesPage"));
const SchoolDepartmentsPage = lazy(() => import("@/pages/staff/SchoolDepartmentsPage"));
const SchoolUsersPage = lazy(() => import("@/pages/staff/SchoolUsersPage"));
const SchoolRecruitingPage = lazy(() => import("@/pages/staff/SchoolRecruitingPage"));
const SchoolAnalyticsPage = lazy(() => import("@/pages/staff/SchoolAnalyticsPage"));
const SchoolSettingsPage = lazy(() => import("@/pages/staff/SchoolSettingsPage"));
const PaymentResultPage = lazy(() => import("@/pages/staff/PaymentResultPage"));
// iter55 · Phase 2
const SchoolAthleteProfilePage = lazy(() => import("@/pages/staff/SchoolAthleteProfilePage"));
const SchoolStoriesPage = lazy(() => import("@/pages/staff/SchoolStoriesPage"));
// iter56 · Phase 3
const SchoolMediaPage = lazy(() => import("@/pages/staff/SchoolMediaPage"));
// iter644 · Dedicated per-game folder page — /school/games/:eventId
const SchoolGameFolderPage = lazy(() => import("@/pages/staff/SchoolGameFolderPage"));
// iter645 · Athlete-private feed for one game — view, download, share,
// promote to their public FLYT profile. Route: /my-media/games/:eventId
const MyGameMediaPage = lazy(() => import("@/pages/MyGameMediaPage"));
// iter654 · FLYT Athlete Media Hub — center Wings tab. School games
// + athlete-created games in one grid; contributor upload links for
// personal events; walkthrough+CTAs when there's no media yet.
const AthleteMediaHubPage = lazy(() => import("@/pages/AthleteMediaHubPage"));
const AthleteMediaEventPage = lazy(() => import("@/pages/AthleteMediaEventPage"));
const AthleteGameFolderPage = lazy(() => import("@/pages/AthleteGameFolderPage"));
// iter770 · Athlete-side approve/decline queue for contributor video —
// nothing here reaches the Media Hub until the athlete acts on it.
const AthleteVideoReviewPage = lazy(() => import("@/pages/AthleteVideoReviewPage"));
// Public contributor upload link — no auth. Renders outside the SPA
// shell (no bottom nav, no cookies) so anyone with the token can
// drop photos into the athlete's event.
const ContributorUploadPage = lazy(() => import("@/pages/ContributorUploadPage"));
const UploadLinkResolver = lazy(() => import("@/pages/UploadLinkResolver"));
// iter651 · Athlete's private per-school portal — every photo the
// school has tagged them in, grouped by game with year+sport filters.
const SchoolPortalPage = lazy(() => import("@/pages/SchoolPortalPage"));
const SchoolCalendarPage = lazy(() => import("@/pages/staff/SchoolCalendarPage"));
const SchoolBrandPage = lazy(() => import("@/pages/staff/SchoolBrandPage"));
// iter614 · FLYT Media Network — event-based media pipeline
const CreatorDashboardPage = lazy(() => import("@/pages/staff/CreatorDashboardPage"));
const CreatorEventUploadPage = lazy(() => import("@/pages/staff/CreatorEventUploadPage"));
const SchoolEventCommandCenter = lazy(() => import("@/pages/staff/SchoolEventCommandCenter"));
const GameGalleryPage = lazy(() => import("@/pages/GameGalleryPage"));
const CreatorPortfolioPage = lazy(() => import("@/pages/CreatorPortfolioPage"));
// iter57 · Public school landing (fan-facing school roster)
const SchoolNetworkPage = lazy(() => import("@/pages/SchoolNetworkPage"));
import { StaffAuthProvider } from "@/pages/staff/StaffAuthContext";
const FounderInboxPage = lazy(() => import("@/pages/FounderInboxPage"));

// iter145 · Hidden persona landing pages (for-athletes/agents/brands/programs) + Guides cluster
const ForAthletesLandingPage = lazy(() => import("@/pages/landing/ForAthletesLandingPage"));
const ForAgentsLandingPage = lazy(() => import("@/pages/landing/ForAgentsLandingPage"));
const FlytFunnelLandingPage = lazy(() => import("@/pages/landing/FlytFunnelLandingPage"));
const ForBrandsLandingPage = lazy(() => import("@/pages/landing/ForBrandsLandingPage"));
const ForProgramsLandingPage = lazy(() => import("@/pages/landing/ForProgramsLandingPage"));
const SchoolPartnershipsPage = lazy(() => import("@/pages/landing/SchoolPartnershipsPage"));
const GuidesIndexPage = lazy(() => import("@/pages/GuidesIndexPage"));
const GuidePage = lazy(() => import("@/pages/GuidePage"));

// Founder-only floating live tracking widget — server-gated.
const FounderLiveStats = lazy(() => import("@/components/FounderLiveStats"));
const PayoutNudgePopup = lazy(() => import("@/components/connect/PayoutNudgePopup"));

// iter816 · Jay: LiveAlert (the in-app breaking-news popup) disabled —
// see LiveAlertGate below. Commented out rather than deleted so both
// halves of the feature restore together in one place.
// const LiveAlert = lazy(() => import("@/components/LiveAlert"));
const NativePushPrimer = lazy(() => import("@/components/NativePushPrimer"));

// Route Suspense fallback — renders a real dark skeleton (not a blank
// black screen) so users see structural placeholders while the lazy
// chunk downloads. Under 300ms most of the time, but on cold cache
// this keeps the app feeling like it's already loading, not stalled.
const RouteFallback = () => <PageSkeleton />;

// Register the push Service Worker exactly once on app boot. We don't
// prompt for permission here — that happens later via the
// PushReminderPrompt mid-article. We just want the SW installed so it
// can receive `push` events when a notification arrives.
const PushBootstrap = () => {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    import('./utils/webPush').then((m) => {
      try { m.registerServiceWorker(); } catch (_) { /* ignore */ }
      // Handle "continue-reading" messages bounced from the SW when the
      // user taps a notification while a tab is already open.
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.addEventListener('message', (event) => {
          if (event?.data?.type === 'continue-reading' && event?.data?.url) {
            try { window.history.pushState({}, '', event.data.url); window.dispatchEvent(new PopStateEvent('popstate')); } catch (_) { /* ignore */ }
          }
        });
      }
    }).catch(() => { /* ignore */ });
    // Inside the iOS shell (Capacitor) also register for APNs push so
    // a single send fans out to phone + browser. No-ops on the web.
    import('./lib/nativeBridge').then((m) => {
      try { m.registerNativePush(`${process.env.REACT_APP_BACKEND_URL}/api`); } catch (_) { /* ignore */ }
      // Confirm this JS bundle launched cleanly so the Capgo Live
      // Updater doesn't roll back to the previous one. MUST happen
      // inside the first 10s of boot or the bundle is marked broken.
      try { m.confirmLiveBundle(); } catch (_) { /* ignore */ }
      // Intercept Universal Links (Google OAuth redirect + shared
      // athlete/story URLs) so they open inside the app instead of
      // bouncing to Safari.
      try { m.bindUniversalLinks(); } catch (_) { /* ignore */ }
      // "Smart claim": first-launch clipboard hop that lands a brand-new
      // install straight on the athlete's upload link they tapped.
      try { m.checkDeferredInvite(); } catch (_) { /* ignore */ }
    }).catch(() => { /* ignore */ });
    // iter880 · App Health — one "app opened" per native launch, tagged
    // with the App Store version, so a release that never opens (or
    // never reaches users) shows up on the 7 AM dashboard.
    import('./lib/journey').then((m) => { try { m.journeyAppOpen(); } catch (_) { /* ignore */ } }).catch(() => {});
  }, []);
  return null;
};

// Applies a body-level class for FLYT (dark), Builders (dark-amber), Dreams League (light)
const ChannelTheme = () => {
  const channel = useChannel();
  useEffect(() => {
    let cls = 'channel-dl';
    if (channel.id === 'flyt') cls = 'channel-flyt';
    else if (channel.id === 'dreams_visionary') cls = 'channel-visionary';
    document.body.classList.remove('channel-flyt', 'channel-dl', 'channel-visionary');
    document.body.classList.add(cls);
    return () => { document.body.classList.remove(cls); };
  }, [channel.id]);
  return null;
};

// Re-keys (and thus re-mounts) its children whenever the active channel
// changes, so the .channel-fade CSS animation plays on every tab switch.
const ChannelTransition = ({ children }) => {
  const channel = useChannel();
  return (
    <div key={channel.id} className="channel-fade" data-testid={`channel-transition-${channel.id}`}>
      {children}
    </div>
  );
};

// Smooth cross-route fade — re-keys on `pathname` so the 180ms
// `.page-fade-in` animation from index.html runs on every navigation.
// Query strings are intentionally ignored so filter/tab tweaks that
// only change `?param=` don't trigger a re-mount flash. Users on
// `prefers-reduced-motion` get an instant swap via the media query.
const PageTransition = ({ children }) => {
  const location = useLocation();
  return (
    <div key={location.pathname} className="page-fade-in" data-testid="page-transition">
      {children}
    </div>
  );
};

// New `/` landing — Network surface (athlete access) up top, with the
// dark-cascaded editorial Homepage stacked underneath. The Stories tab
// (`/network`) still renders the full standalone (light) Homepage.
//
// NetworkPage is rendered in `isHomeMode` so it knows to (1) lift the
// "Follow These Athletes" row to the very top, and (2) inject the
// "FLYT BUILT STORIES" headline in the mid-flow slot where that row
// used to live. No external header here anymore — keeps the order in
// one place inside NetworkPage.
const HomeIndex = () => {
  // iter354 · Wait for the auth check before deciding where the compact
  // Homepage mounts. Previously `user` started null on every boot, so
  // signed-in users mounted <Homepage/> at the BOTTOM (full fetch), then
  // auth resolved and it re-mounted INSIDE the slot (full re-fetch) —
  // doubling every story request on native iOS launch.
  const { user, loading } = useUserAuth();
  // iter517 · When the app is loaded on a personalized FLYT ID™
  // subdomain (jaxon.flythouse.com), silently mount the AthleteSharePage
  // for that slug instead of the network home. URL bar stays clean —
  // no client-side redirect, so users see `jaxon.flythouse.com/` matching
  // exactly what the athlete copied and shared. Runs AFTER hooks so
  // rules-of-hooks stays clean.
  const _sub = (typeof window !== 'undefined')
    ? readFlytIdFromHost()
    : null;
  if (_sub) {
    return <AthleteSharePage slugOverride={_sub} />;
  }
  const isSignedIn = !!user;
  // Signed-out users opening the NATIVE app's home tab get a real
  // "Welcome to FLYT" moment (Sign Up / Sign In) instead of the public
  // web feed — that feed is still exactly what a browser visitor sees
  // on flythouse.com, and what a shared story/upload/profile link
  // opens to on EITHER platform (those are separate routes, never
  // this one). Waits for the auth check to resolve so a signed-in
  // user's home never flashes this gate for a frame on cold boot.
  if (!loading && !isSignedIn && isNativeApp()) {
    return <WelcomeGate />;
  }
  // iter718 · Logged-in ATHLETES get a home built around their account
  // (Launch Pad → live game → FLYT-link analytics → recent photos).
  // Everyone else keeps the network/Pulse home exactly as-is.
  const isAthlete = !!(user && (user.athlete_slug || user.linked_athlete || user.linked_athlete_slug));
  if (!loading && isAthlete) {
    return (
      <div data-testid="home-index">
        <ReviewerUpgradeCTA />
        <ReviewerQuickAccessPanel />
        <AthleteHome />
      </div>
    );
  }
  return (
    <div data-testid="home-index">
      {/* iter538 · Apple App Review reviewer-only CTA. No-op for every
          other account (returns null); giant golden upgrade pill for
          `applereview@flythouse.com` so the reviewer can reach the
          StoreKit paywall in one tap from the home screen. */}
      <ReviewerUpgradeCTA />
      {/* iter547 · Reviewer Compliance Checklist. Sits directly under
          the UPGRADE CTA, lists every guideline-required feature
          (Report, Block, Delete Account, Restore, Privacy, Terms,
          Sign Out) with tap-to-navigate. Reviewer-only, returns null
          for every other user. */}
      <ReviewerQuickAccessPanel />
      <NetworkPage
        isHomeMode
        renderAfterBreaking={isSignedIn ? <Homepage compact /> : null}
      />
      {!loading && !isSignedIn && <Homepage compact />}
    </div>
  );
};

// Reserved top-level path segments that must NEVER be treated as an
// athlete slug — every existing route + a defensive buffer for future
// routes. Anything not in this set is passed to AthleteNetworkPage,
// which itself 404s cleanly if the slug doesn't match an athlete row.
const RESERVED_TOP_LEVEL = new Set([
  '', 'network', 'stories', 'collections', 'access', 'athletes', 'brands',
  'brand', 'the-network', 'programs', 'about', 'contact', 'profile',
  'login', 'signup', 'admin', 'inbox', 'search', 'messages', 'portal',
  'proposal', 'privacy', 'legal', 'secret', 'founder', 'agent', 'flyt',
  'visionary', 'auth', 'api', 'settings', 'notifications', 'terms',
  'help', 'support', 'faq', 'press', 'careers', 'jobs', 'team',
  'athlete', 'client', 'school', 'staff', 'school-network', 'pulse',
  // iter145 · SEO landing pages + guides cluster
  'for-athletes', 'for-agents', 'for-brands', 'for-programs', 'guides',
  // iter236 · School Partnerships media-partner pitch
  'school-partnerships',
  // iter229u · ESPN claim → auto-create-account flow
  'claim',
  // iter568 · Admin dashboard moved to /Penelopesims — reserved so
  // the /:slug catchall doesn't route this to an athlete profile.
  // The `/admin` slug stays reserved too so hitting it returns the
  // same 404 as the AdminOnlyRoute gate — no leak that the admin
  // moved.
  'penelopesims',
]);

// Phase 2 · flythouse.com/<slug> · permanent athlete URL.
// Delegates to AthleteNetworkPage when the URL segment is a real slug,
// or renders NotFound for reserved words so we never collide with an
// existing route. This is the "one URL forever" that replaces Linktree.
const TopLevelAthleteRoute = () => {
  const { slug } = useReactRouterParams();
  const clean = (slug || '').toLowerCase().split('?')[0];
  if (!clean || RESERVED_TOP_LEVEL.has(clean)) {
    return <div className="min-h-screen bg-black text-white flex items-center justify-center text-sm text-white/50">Page not found.</div>;
  }
  return <AthleteNetworkPage />;
};

// Dreams-League-only iOS-style live notification (breaking news / new stories).
// Sits at the top of the viewport and silently no-ops on FLYT pages.
// iter816 · Jay: disabled entirely — the in-app popup was showing up
// repeatedly and he doesn't want it right now. Original gating logic
// commented out below so it's a one-line restore if that changes later.
const LiveAlertGate = () => {
  return null;
  // const channel = useChannel();
  // if (channel?.id !== 'dreams_league') return null;
  // return (
  //   <Suspense fallback={null}>
  //     <LiveAlert />
  //   </Suspense>
  // );
};

function AppRoutes() {
  usePageTracking();
  useMessagesBackTracker();
  const location = useLocation();

  // iter359 · Global hover/touch prefetch for FLYT link + FLYT ID™ +
  // Network profile pages. Any <a href> or <Link to> that resolves
  // to /l/<slug>, /l/agent/<slug>, or /network/<slug> gets its JSON +
  // route chunk warmed the moment the user's finger/cursor grazes it.
  // Combined with the backend's 60s CDN cache, tap-to-paint feels
  // near-instant.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let scheduled = null;
    const dispatch = async (href) => {
      try {
        const { prefetchFlytLink, prefetchAgentLink, prefetchNetworkProfile } = await import('./utils/prefetch');
        // /l/agent/<slug>
        const agentMatch = href.match(/\/l\/agent\/([a-z0-9-]{1,120})(?:$|[?#/])/i);
        if (agentMatch) return prefetchAgentLink(agentMatch[1]);
        // /l/<slug>  (athlete bio link — must exclude /l/agent above)
        const linkMatch = href.match(/\/l\/([a-z0-9-]{1,120})(?:$|[?#/])/i);
        if (linkMatch && !href.startsWith('/l/agent/')) return prefetchFlytLink(linkMatch[1]);
        // /network/<slug>
        const netMatch = href.match(/\/network\/([a-z0-9-]{1,120})(?:$|[?#/])/i);
        if (netMatch) return prefetchNetworkProfile(netMatch[1]);
      } catch { /* ignore — prefetch is best-effort */ }
    };
    const onEnter = (e) => {
      const el = e.target?.closest?.('a[href]');
      if (!el) return;
      const href = el.getAttribute('href') || '';
      if (!href.startsWith('/')) return;
      // Debounce a beat so a fast mouse sweep doesn't fan out dozens
      // of parallel requests.
      clearTimeout(scheduled);
      scheduled = setTimeout(() => dispatch(href), 60);
    };
    document.addEventListener('pointerenter', onEnter, { capture: true });
    document.addEventListener('touchstart',   onEnter, { capture: true, passive: true });
    return () => {
      clearTimeout(scheduled);
      document.removeEventListener('pointerenter', onEnter, { capture: true });
      document.removeEventListener('touchstart',   onEnter, { capture: true });
    };
  }, []);
  // iter154 · Embed mode. When landing pages iframe real product routes
  // (e.g. /network/ozzie-albies?embed=1) we strip the global chrome so
  // the frame only shows the page body — no navbar, no footer, no
  // bottom-nav, no floating widgets. Enables authentic "product
  // preview" screenshots without the maintenance cost of PNGs.
  //
  // We also treat every public bio-link route (`/l/*` — athlete and
  // agent share pages) as chrome-free. When an athlete posts their
  // Flythouse link in their Instagram bio, the destination should
  // feel like THEIR home, not a Flythouse sub-page. So no top navbar,
  // no bottom tab bar, no floating widgets. A minimal "Powered by
  // Flythouse" strip is rendered inside each share page instead.
  const isBioLinkPath = location.pathname.startsWith('/l/');
  // iter490 · /start is a standalone ad-target landing — no site
  // chrome so the athlete sees a full-bleed onboarding, not the
  // FLYT League nav they'd see on the main site. Same treatment
  // as bio-link share pages.
  const isStandaloneOnboarding = location.pathname === '/start';
  const embedMode =
    new URLSearchParams(location.search).get('embed') === '1' || isBioLinkPath || isStandaloneOnboarding;
  // iter688v · The public contributor upload flow (/upload/:token, /u/:slug)
  // is a focused, single-purpose funnel — suppress the App-Store promo
  // popup + cookie banner there so they can't overlap the "Allow photo
  // access" / upload CTAs on mobile first paint.
  const isPublicUploadRoute = /^\/(upload|u)\//.test(location.pathname);

  // iter · Hide the top FLYT banner (Navbar) on the main tab pages —
  // home, stories, media hub, search — for a cleaner look. Keep it on
  // profile & all other pages. A safe-area spacer takes its place so
  // page content still clears the iOS notch / status bar.
  const _navP = location.pathname.replace(/\/+$/, '') || '/';
  const hideTopNav = (
    _navP === '/' ||
    /(^|\/)stories(\/|$)/.test(_navP) ||
    _navP.startsWith('/me/media') ||
    _navP === '/search' || _navP.startsWith('/search/') ||
    _navP === '/demo' || _navP.startsWith('/demo/') ||
    _navP === '/build' || _navP.startsWith('/build/') ||
    // iter · Network PROFILE pages go chrome-less for a native-app feel —
    // no top FLYT / Pulse / hamburger bar. Covers the athlete profile
    // (/network/:slug + /network/:slug/media) and the visionary profile
    // (/visionary/network/:slug). Excludes the league page and the
    // network index (which redirects to /).
    /^\/network\/(?!league(?:\/|$))[^/]+/.test(_navP) ||
    /^\/visionary\/network\/[^/]+/.test(_navP)
  );

  // iter · On these otherwise chrome-less pages, restore the top FLYT
  // Navbar (with logo + hamburger / login) for DESKTOP WEB visitors so
  // first-timers who land on a shared profile can actually navigate the
  // site. The native iOS/iPad app shell stays fully chrome-less (it has
  // its own bottom nav), and small web viewports keep the clean look +
  // safe-area spacer. `lg:` (>=1024px) = desktop.
  let _isNativeShell = false;
  try { _isNativeShell = isNativeApp(); } catch { _isNativeShell = false; }
  // /demo has its own standalone sales header — keep it fully chrome-less.
  const _isDemoPage = _navP === '/demo' || _navP.startsWith('/demo/');
  // iter840 · The /build ad-funnel pages have their own top bar (FLYT
  // wings + Build Now) — Jay wants them to feel like a standalone
  // landing, so suppress the sitewide Navbar (PULSE/hamburger/FLYT
  // banner) on EVERY viewport, desktop web included, same as /demo.
  const _isFunnelPage = _navP === '/build' || _navP.startsWith('/build/');

  // ── Emergent Google OAuth callback ──────────────────────────────
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  // Detect session_id in the URL fragment synchronously (NOT in useEffect)
  // so the AuthCallback runs before any other route effects fire.
  //
  // We also catch the bare `/auth/callback` path (with or without the
  // fragment) — that's the Universal-Link target used by the iOS app
  // so Google → Emergent Auth can bounce the redirect back into the
  // Capacitor WKWebView instead of a broken `capacitor://localhost` URL.
  if (location.hash?.includes('session_id=') || location.pathname === '/auth/callback') {
    return <AuthCallback />;
  }
  // iter119 · White-labeled Google OAuth callback. Google redirects
  // here with `?code=...&state=...` after the user consents on the
  // Flythouse consent screen. Handle it before any other route so no
  // guarded page tries to redirect the user away mid-exchange.
  if (location.pathname === '/auth/google') {
    return <GoogleAuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
      <ChannelTheme />
      <SEOHead />
      <MetaRouteAnalytics />
      <LoginModal />
      <ClaimCodeSheet />
      <ApplyToBeFeaturedModal />
      <PersonaWelcomeModal />
      <ReferralCodeCatcher />
      {/* iter548 · Reviewer coach-mark banner. Reads `?reviewer-guide=X`
          from the URL and paints a golden top pill with the exact
          instruction for that guideline. Reviewer-only (returns null
          otherwise). Mounted OUTSIDE the <Routes> so it renders on
          every route including standalone pages like /upgrade, /me/blocked,
          and /profile. */}
      <ReviewerGuideBanner />
      {/* iter639 · Sonner Toaster mounted globally OUTSIDE <Routes>
          so every route tree (public /*, /school/*, /staff/*, /admin,
          /Penelopesims, standalone pages) shows toast feedback. Prior
          location nested inside the /* layout element caused School OS
          pages to silently swallow every toast.success/error. */}
      <Toaster
        position="top-center"
        richColors
        closeButton={false}
        offset={{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
        mobileOffset={{ top: 'calc(env(safe-area-inset-top, 0px) + 12px)' }}
      />
      {/* iter321 · DobGuard temporarily disabled — the /verify-age
          endpoint was returning "Verification failed" for legitimate
          adult DOBs and locking real users out of the site before
          they could even browse. Kept the import + component intact so
          we can re-enable after the backend bug is triaged. */}
      {/* <DobGuard /> */}
      {/* iter862 · Full welcome walkthrough for a first-time athlete
          signup (or manually replayed via the hamburger menu's
          "Walkthrough" item). Mounted globally — not inside a single
          page — because it spans both Home ("/") and Profile
          ("/profile") and needs to survive the navigation between
          them. Self-gates to athlete accounts with a linked slug;
          renders nothing otherwise. */}
      <WelcomeSpotlightTour />
      <Suspense fallback={<RouteFallback />}>
        {/* iter553 · Vanity apex shortcut. When the SPA is served from
            REACT_APP_VANITY_APEX_HOST (e.g. `flyt.co`), a single-segment
            URL like `flyt.co/willie` should render Willie's FLYT ID™
            share page directly — no `/l/` prefix, one path segment,
            clean brand primitive. `getVanityApexSlug()` returns the
            slug ONLY on the vanity host AND when the path segment
            isn't a reserved system route. Anywhere else it returns
            null and we fall through to the normal Routes below. */}
        <VanityApexShortcut fallback={
        <Routes>
          {/* Public site routes with Navbar and Footer */}
          <Route
            path="/*"
            element={
              <>
                {!embedMode && (hideTopNav
                  ? ((_isNativeShell || _isDemoPage || _isFunnelPage)
                      ? <div aria-hidden="true" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }} />
                      : (
                        <>
                          {/* Small web viewports (phone/tablet): keep it clean */}
                          <div className="lg:hidden" aria-hidden="true" style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }} />
                          {/* Desktop web: show the FLYT navbar so visitors can navigate */}
                          <div className="hidden lg:block"><Navbar /></div>
                        </>
                      ))
                  : <Navbar />)}
                {!embedMode && <LiveAlertGate />}
                {!embedMode && <PWAInstallPrompt />}
                {!embedMode && <BackButton />}
                <ChannelTransition>
                <PageTransition>
                <Routes>
                  {/* Dreams League channel */}
                  {/* iter490 · Athlete Onboarding — standalone ad-target
                      landing at /start. Registered ABOVE `/` so it wins
                      the route match. Aliases redirect to /start so
                      any ad copy variant works. Preview + prod share
                      one code path. */}
                  <Route path="/start" element={<FlytIdOnboardingPage />} />
                  <Route path="/create" element={<Navigate replace to="/start" />} />
                  <Route path="/claim" element={<Navigate replace to="/start" />} />
                  <Route path="/id" element={<Navigate replace to="/start" />} />
                  {/* `/` is now the Network-first home (athlete access).
                      Old Flyt stories page lives at `/network` (Stories tab). */}
                  <Route path="/" element={<HomeIndex />} />
                  {/* iter258 · The old standalone stories Homepage is retired —
                      exact /network now lands on the Network home ("/").
                      Athlete profiles at /network/:slug are untouched. */}
                  <Route path="/network" element={<Navigate replace to="/" />} />
                  <Route path="/team/:name" element={<TeamAthletesPage />} />
                  <Route path="/network/league/:slug" element={<NetworkLeaguePage />} />
                  <Route path="/network/:slug" element={<AthleteNetworkPage />} />
                  <Route path="/network/:slug/media" element={<AthleteCareerMediaPage />} />
                  <Route path="/l/:slug" element={<AthleteSharePage />} />
                  <Route path="/stories" element={<StoriesIndex />} />
                  <Route path="/stories/:slug" element={<StoryPage />} />
                  <Route path="/collections" element={<CollectionsIndexPage />} />
                  <Route path="/collections/:slug" element={<CollectionPage />} />
                  <Route path="/access" element={<Navigate replace to="/for-athletes" />} />
                  <Route path="/the-network" element={<TheNetworkPage />} />
                  <Route path="/athletes" element={<AthletesPage />} />
                  <Route path="/discover" element={<DiscoverFlytPage />} />
                  <Route path="/brands" element={<BrandsPage />} />
                  <Route path="/brands/submit-ad" element={<SubmitAdPage />} />
                  <Route path="/brand/:slug" element={<BrandNetworkPage />} />
                  <Route path="/portal" element={<ClientPortalPage />} />
                  <Route path="/messages" element={<ClientMessagesPage />} />
                  <Route path="/programs" element={<ProgramsPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  {/* iter816 · Dedicated white-background Network Profile
                      screen for the plain athlete persona — BottomNav
                      routes the profile tab here instead of /profile,
                      which stays untouched for every other persona. */}
                  <Route path="/network-profile" element={<NetworkProfilePage />} />
                  {/* iter645 · Athlete-private game media */}
                  <Route path="/my-media/games/:eventId" element={<MyGameMediaPage />} />
                  {/* iter654 · FLYT Media Hub (athlete center tab). */}
                  <Route path="/me/media" element={<AthleteMediaHubPage />} />
                  <Route path="/me/media/events/:eventId" element={<AthleteMediaEventPage />} />
                  <Route path="/me/media/games/:eventId" element={<AthleteGameFolderPage />} />
                  {/* iter770 · Approve/decline queue for contributor video */}
                  <Route path="/me/media/videos/review" element={<AthleteVideoReviewPage />} />
                  {/* Public contributor upload — no auth. */}
                  <Route path="/upload/:token" element={<ContributorUploadPage />} />
                  {/* iter688n · Vanity upload link flytid.co/u/{slug} */}
                  <Route path="/u/:slug" element={<UploadLinkResolver />} />
                  {/* iter651 · Athlete-private school portal */}
                  <Route path="/my-media/school/:orgId" element={<SchoolPortalPage />} />
                  {/* iter302 · FLYT Ambassador Program */}
                  <Route path="/ambassador" element={<AmbassadorDashboard />} />
                  <Route path="/ambassador-signup" element={<AmbassadorSignupPage />} />
                  <Route path="/texas-hs/:slug" element={<TxHsDistrictPage />} />
                  <Route path="/guardian-consent/:token" element={<GuardianConsentPortalPage />} />
                  <Route path="/partners" element={<PartnersPage />} />
                  <Route path="/r/:code" element={<AmbassadorRedirect />} />
                  <Route path="/founder/training" element={<FounderTrainingPage />} />
                  <Route path="/legal/creator-agreement" element={<CreatorAgreementPage />} />
                  <Route path="/earn-program/terms" element={<EarnProgramTermsPage />} />
                  <Route path="/athlete/me" element={<AthleteMePage />} />
                  <Route path="/athlete/dashboard" element={<AthleteCareerHomePage />} />
                  <Route path="/agent/dashboard" element={<AgentDashboardPage />} />
                  <Route path="/l/agent/:slug" element={<AgentLinkPublicPage />} />
                  <Route path="/kit/:token" element={<BrandReachOutKitPage />} />
                  <Route path="/agent-invite/:token" element={<AgentInviteAcceptPage />} />
                  <Route path="/athlete/invite/:token" element={<AthleteInviteAcceptPage />} />
                  <Route path="/roster/messages" element={<RosterMessagesPage />} />
                  <Route path="/athlete/messages" element={<AthleteMessagesPage />} />
                  <Route path="/agent/signup" element={<AgentSignupPage />} />
                  <Route path="/brand/dashboard" element={<BrandDashboardPage />} />
                  <Route path="/brand/signup" element={<BrandSignupPage />} />
                  {/* iter320 · Recruiter Persona */}
                  <Route path="/recruiter/signup" element={<RecruiterSignupPage />} />
                  <Route path="/recruiter/dashboard" element={<RecruiterDashboardPage />} />
                  <Route path="/recruiter/athletes/:slug" element={<RecruiterAthleteDetailPage />} />
                  <Route path="/welcome" element={<WelcomePage />} />
                  <Route path="/flyt-studios" element={<FlytStudiosPage />} />
                  <Route path="/search" element={<SearchPage />} />
                  <Route path="/pulse" element={<PulsePage />} />
                  <Route path="/inbox" element={<FounderInboxPage />} />
                  <Route path="/secret" element={<SecretUnlockPage />} />
                  <Route path="/secret/project" element={<SecretRevealPage />} />
                  <Route path="/secret/story/:slug" element={<SecretStoryPage />} />

                  {/* iter145 · Hidden persona landing pages (NOT linked from nav). Used for paid ads. */}
                  {/* iter153 · A/B variants — /for-athletes/a, /for-athletes/b, etc. */}
                  <Route path="/for-athletes" element={<ForAthletesLandingPage />} />
                  <Route path="/for-athletes/:variant" element={<ForAthletesLandingPage />} />
                  <Route path="/for-agents" element={<ForAgentsLandingPage />} />
                  <Route path="/for-agents/:variant" element={<ForAgentsLandingPage />} />
                  {/* Real-screenshot ad funnel page — what paid ads point at.
                      /build = parent hero copy, /build/athlete = athlete hero copy. */}
                  <Route path="/build" element={<FlytFunnelLandingPage variant="parent" />} />
                  <Route path="/build/parent" element={<FlytFunnelLandingPage variant="parent" />} />
                  <Route path="/build/athlete" element={<FlytFunnelLandingPage variant="athlete" />} />
                  {/* iter852 · CONCEPT preview — new stat cards + "Connected"
                      section. Live /build pages stay untouched. */}
                  <Route path="/build/concept" element={<FlytFunnelLandingPage variant="parent" concept />} />
                  <Route path="/build/concept/athlete" element={<FlytFunnelLandingPage variant="athlete" concept />} />
                  <Route path="/for-brands" element={<ForBrandsLandingPage />} />
                  <Route path="/for-brands/:variant" element={<ForBrandsLandingPage />} />
                  <Route path="/for-programs" element={<ForProgramsLandingPage />} />
                  <Route path="/for-programs/:variant" element={<ForProgramsLandingPage />} />
                  {/* iter236 · Media-partner pitch for high schools */}
                  <Route path="/school-partnerships" element={<SchoolPartnershipsPage />} />
                  {/* iter145 · Long-tail SEO guide cluster */}
                  <Route path="/guides" element={<GuidesIndexPage />} />
                  <Route path="/guides/:slug" element={<GuidePage />} />

                  {/* Dreams Visionary channel — separate data, same layout, gradient theme */}
                  <Route path="/visionary" element={<Homepage />} />
                  <Route path="/visionary/network" element={<NetworkPage />} />
                  <Route path="/visionary/network/:slug" element={<AthleteNetworkPage />} />
                  <Route path="/visionary/stories" element={<StoriesIndex />} />
                  <Route path="/visionary/stories/:slug" element={<StoryPage />} />
                  <Route path="/visionary/voices" element={<AthletesPage />} />
                  <Route path="/visionary/about" element={<AboutPage />} />
                  <Route path="/visionary/contact" element={<ContactPage />} />
                  <Route path="/visionary/profile" element={<ProfilePage />} />

                  {/* FLYT channel — same components, channel context drives content */}
                  <Route path="/flyt" element={<Homepage />} />
                  <Route path="/flyt/stories" element={<StoriesIndex />} />
                  <Route path="/flyt/stories/:slug" element={<StoryPage />} />
                  <Route path="/flyt/voices" element={<AthletesPage />} />
                  <Route path="/flyt/brands" element={<BrandsPage />} />
                  <Route path="/flyt/about" element={<AboutPage />} />
                  <Route path="/flyt/contact" element={<ContactPage />} />
                  <Route path="/flyt/process" element={<FlytProcessPage />} />
                  <Route path="/flyt/clients/:slug" element={<FlytClientPage />} />
                  <Route path="/flyt/case-studies" element={<FlytCaseStudiesPage />} />
                  <Route path="/flyt/profile" element={<ProfilePage />} />

                  {/* Phase 2 · flythouse.com/<slug> — permanent athlete URL.
                      MUST stay as the last route so all explicit paths take
                      priority. `TopLevelAthleteRoute` filters out reserved
                      words (about, contact, brand, network, admin, etc.) so
                      only real athlete slugs actually hit the profile. */}
                  {/* /demo lives in the outer chrome-less route group below. */}
                  <Route path="/:slug" element={<TopLevelAthleteRoute />} />
                </Routes>
                </PageTransition>
                </ChannelTransition>
                {!embedMode && <Footer />}
                {!embedMode && <GlobalUploadBar />}
                {!embedMode && <BottomNav />}
                {/* iter229 · SwipeNavigator disabled — Jay reported the
                    horizontal swipe on the network home was unexpectedly
                    navigating to `/network` (the standalone Homepage
                    route). The BottomNav pill is enough — no gesture
                    hijack. Leaving the import so the component can be
                    re-enabled later without a merge. */}
                {false && !embedMode && <SwipeNavigator />}
                {/* iter239 · First-visit cookie consent banner. Stores the
                    user's choice in localStorage under `flyt_cookie_consent`
                    so the banner never nags again once dismissed. Emits a
                    `cookieconsent-change` event so analytics loaders can
                    gate themselves on consent. */}
                {!embedMode && !isPublicUploadRoute && <CookieConsentBanner />}
                {/* iter579 · Global "Download on the App Store"
                    liquid-glass popup. Web-only, appears 5s after
                    first paint, once-per-tab. Skipped on admin /
                    staff / school / claim routes so it never
                    interrupts a working session. */}
                {!embedMode && !isPublicUploadRoute && <AppStorePromoPopup />}
                {/* Phase 3A · Career Coach floating bubble — now mounted
                    inside `ProfilePage` only (per Jay), so it hovers
                    above the BottomNav profile icon on that view and
                    stays hidden everywhere else. */}
                {!embedMode && (
                  <Suspense fallback={null}>
                    <FounderLiveStats />
                  </Suspense>
                )}
                {/* iter231 · Popup for athletes when their agent has
                    nudged them to set up Stripe payouts. Fires once
                    per session — client polls /api/me/nudges/payout
                    on mount and shows the modal only if the backend
                    returns an unseen row. */}
                {/* iter369 · Payout nudge popup disabled pre-launch. */}
                {false && !embedMode && (
                  <Suspense fallback={null}>
                    <PayoutNudgePopup />
                  </Suspense>
                )}
              </>
            }
          />

          {/* Standalone pages */}
          <Route path="/login" element={<LoginPage />} />
          {/* iter571 · Hidden admin login. `/login` is the public
              login route (staff/clients). `/Penelopesims/login` is
              the SAME component but at an unlinked URL — the only
              way an admin can sign back in after logging out of the
              admin dashboard. Not referenced anywhere in the UI so
              a random visitor probing `/Penelopesims` still hits
              the "Page not found." gate. */}
          <Route path="/Penelopesims/login" element={<LoginPage />} />
          {/* Password reset landing — arrives here from the Resend
              magic-link email with ?token=<xxx>. See ResetPasswordPage. */}
          <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/upgrade" element={<UpgradePage />} />
          <Route path="/support" element={<SupportPage />} />
          <Route path="/picks/:slug" element={<PicksPublicPage />} />
          <Route path="/picks-portal" element={<PicksPortalPage />} />
          <Route path="/claim/espn/:espnId" element={<ClaimAndSignupPage />} />
          <Route path="/founder/match" element={<FounderMatchPage />} />
          {/* iter369 · /payouts route redirects to /profile pre-launch —
              PayoutsPage kept in code but unreachable via UI. */}
          {/* iter870 · Stripe Connect return_url / refresh_url target. */}
          <Route path="/payouts/done" element={<PayoutsReturnPage />} />
          <Route path="/payouts" element={<Navigate to="/profile" replace />} />
          <Route path="/terms" element={<TermsPage />} />
          {/* iter512 · Apple 1.2 UGC — mandatory blocked-users management screen */}
          <Route path="/me/blocked" element={<BlockedUsersPage />} />
          <Route path="/settings/blocked" element={<BlockedUsersPage />} />
          {/* iter514 · Founder-only queue for reviewing UGC reports.
              Nothing auto-actions here — Jay reviews each report and
              picks Dismiss / Remove content / Eject user. */}
          <Route path="/founder/reports" element={<FounderModerationReportsPage />} />
          {/* iter568 · Admin dashboard URL moved from `/admin/*` to
              `/Penelopesims/*` and gated with `AdminOnlyRoute` which
              renders a bare 404 (not a login page) for anyone who
              isn't a verified admin. `/admin/*` paths no longer
              exist in the router — they hit the reserved-slug 404
              via `TopLevelAthleteRoute`, indistinguishable from any
              other invalid URL. Real API security is unchanged and
              lives in `Depends(get_current_admin)` on the server. */}
          <Route
            path="/Penelopesims/reports"
            element={
              <AdminOnlyRoute>
                <FounderModerationReportsPage />
              </AdminOnlyRoute>
            }
          />
          {/* iter515 · Founder-only Sentry diagnostics — hidden route,
              gated on user.is_founder inside the component so non-
              founders (Apple reviewer included) never see it. */}
          <Route path="/founder/sentry-test" element={<FounderSentryTestPage />} />
          {/* iter515-preview · Analytics modal UI review route. Safe
              to remove once the modal has been verified. */}
          <Route path="/preview/analytics" element={<AnalyticsModalPreviewPage />} />
          {/* iter516 · Weekly Insights shareable card — public deep-link
              from the Sunday 6pm push. Athletes screenshot this card
              and forward to their group chats → viral loop. */}
          <Route path="/insights/:share_token" element={<WeeklyInsightsCardPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          {/* Payment routes removed (Stripe deprecated pre-launch). */}
          <Route path="/proposal/:token" element={<ProposalPage />} />
          <Route path="/athlete/portal-token" element={<AthleteDashboardPage />} />

          <Route
            path="/Penelopesims"
            element={
              <AdminOnlyRoute>
                <AdminDashboard />
              </AdminOnlyRoute>
            }
          />
          {/* iter647 · FLYT Team Console (Avengers) — separate auth
              at /avengers/login, permission-gated portal at /avengers.
              Team members log in with their own `dl_console` cookie;
              founder auth is untouched. */}
          <Route path="/avengers/login" element={<TeamLoginPage />} />
          <Route path="/avengers" element={<TeamConsolePage />} />
          <Route
            path="/Penelopesims/share-stats"
            element={
              <AdminOnlyRoute>
                <ShareStatsPage />
              </AdminOnlyRoute>
            }
          />
          {/* iter666 · Athlete Account Doctor — diagnose + repair the
              athlete⇄account link (the cause of "no school banner /
              empty photo folder" reports). */}
          <Route
            path="/Penelopesims/athlete-doctor"
            element={
              <AdminOnlyRoute>
                <AthleteDoctorPage />
              </AdminOnlyRoute>
            }
          />

          {/* iter54 · School OS · Athletic Department operating system.
              Staff auth cookie (`dl_staff`) is separate from admin. */}
          <Route path="/staff/login" element={<StaffLoginPage />} />
          {/* Public interactive "Mockup View" sales demo — chrome-less
              standalone (no Navbar/Footer/transitions), client-side only. */}
          <Route path="/demo" element={<DemoMockupPage />} />
          <Route path="/demo/:token" element={<DemoMockupPage />} />
          <Route path="/staff/invitation/:token" element={<StaffAcceptInvitePage />} />
          <Route path="/school" element={
            <StaffAuthProvider><SchoolDashboardPage /></StaffAuthProvider>
          } />
          <Route path="/school/dashboard" element={
            <StaffAuthProvider><SchoolDashboardPage /></StaffAuthProvider>
          } />
          <Route path="/school/athletes" element={
            <StaffAuthProvider><SchoolAthletesPage /></StaffAuthProvider>
          } />
          <Route path="/school/athletes/:slug" element={
            <StaffAuthProvider><SchoolAthleteProfilePage /></StaffAuthProvider>
          } />
          <Route path="/school/stories" element={
            <StaffAuthProvider><SchoolStoriesPage /></StaffAuthProvider>
          } />
          <Route path="/school/media" element={
            <StaffAuthProvider><SchoolMediaPage /></StaffAuthProvider>
          } />
          <Route path="/school/games/:eventId" element={
            <StaffAuthProvider><SchoolGameFolderPage /></StaffAuthProvider>
          } />
          <Route path="/school/calendar" element={
            <StaffAuthProvider><SchoolCalendarPage /></StaffAuthProvider>
          } />
          {/* iter614 · FLYT Media Network — event command center + creator */}
          <Route path="/school/events/:eventId" element={
            <StaffAuthProvider><SchoolEventCommandCenter /></StaffAuthProvider>
          } />
          <Route path="/creator" element={
            <StaffAuthProvider><CreatorDashboardPage /></StaffAuthProvider>
          } />
          <Route path="/creator/events/:eventId" element={
            <StaffAuthProvider><CreatorEventUploadPage /></StaffAuthProvider>
          } />
          {/* Public game gallery — shareable to fans/parents, no auth. */}
          <Route path="/games/:eventId" element={<GameGalleryPage />} />
          <Route path="/creators/:creatorId" element={<CreatorPortfolioPage />} />
          <Route path="/school/brand" element={
            <StaffAuthProvider><SchoolBrandPage /></StaffAuthProvider>
          } />
          {/* iter57 · Public school landing */}
          <Route path="/school-network/:slug" element={<SchoolNetworkPage />} />
          <Route path="/school/departments" element={
            <StaffAuthProvider><SchoolDepartmentsPage /></StaffAuthProvider>
          } />
          <Route path="/school/users" element={
            <StaffAuthProvider><SchoolUsersPage /></StaffAuthProvider>
          } />
          <Route path="/school/recruiting" element={
            <StaffAuthProvider><SchoolRecruitingPage /></StaffAuthProvider>
          } />
          <Route path="/school/analytics" element={
            <StaffAuthProvider><SchoolAnalyticsPage /></StaffAuthProvider>
          } />
          <Route path="/school/settings" element={
            <StaffAuthProvider><SchoolSettingsPage /></StaffAuthProvider>
          } />
          <Route path="/payment/success" element={<PaymentResultPage />} />
          <Route path="/payment/cancel" element={<PaymentResultPage cancelled />} />
        </Routes>
        } />
      </Suspense>
    </>
  );
}

// iter553 · Vanity apex router shortcut. If the SPA is loaded from the
// vanity apex host (`flyt.co`) AND the current path is a single segment
// that isn't a reserved system route, render AthleteSharePage directly
// with the slug. Otherwise render the `fallback` (normal <Routes/>).
// Deleting this component + its call site reverts everything to the
// legacy `/l/{slug}` scheme with zero drama.
function VanityApexShortcut({ fallback }) {
  const slug = getVanityApexSlug();
  if (slug) {
    return <AthleteSharePage slugOverride={slug} />;
  }
  return fallback;
}

function App() {
  // iter270 · After the app settles, warm the hottest lazy route chunks
  // during idle time so the first tap on Stories / a story card / Pulse /
  // Search renders instantly on mobile (no hover events there to trigger
  // the hover-prefetch path).
  useEffect(() => {
    const warm = () => {
      import("@/pages/StoriesIndex").catch(() => {});
      import("@/pages/StoryPage").catch(() => {});
      import("@/pages/PulsePage").catch(() => {});
      import("@/pages/SearchPage").catch(() => {});
    };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(warm, { timeout: 6000 });
      return undefined;
    }
    const t = setTimeout(warm, 3500);
    return () => clearTimeout(t);
  }, []);

  // iter154 · Embed mode — quick early check to skip launch splash
  // and pull-to-refresh when the app is iframed from a landing page.
  // iter217 · Same treatment for `/l/*` bio-link routes so athlete
  // share pages don't flash a Flythouse launch splash or pull-to-
  // refresh — visitors should feel like they're on the athlete's
  // own site, not inside our app shell.
  const embedMode = typeof window !== 'undefined' &&
    (new URLSearchParams(window.location.search).get('embed') === '1'
      || window.location.pathname.startsWith('/l/'));
  return (
    <div className="App">
      <ErrorBoundary>
        <HelmetProvider>
          <BrowserRouter>
            <ChannelProvider>
              <AuthProvider>
                <UserAuthProvider>
                  <PaywallProvider>
                  <UploadManagerProvider>
                  {!embedMode && <PushBootstrap />}
                  {!embedMode && (
                    <Suspense fallback={null}>
                      <NativePushPrimer />
                    </Suspense>
                  )}
                  {/* Splash only plays on homepage landings — shared deep
                      links (athlete profiles, bio links) open straight to
                      content with zero intro delay. */}
                  {!embedMode && window.location.pathname === '/' && <LaunchSplash />}
                  {!embedMode && <PullToRefresh />}
                  <AppRoutes />
                  </UploadManagerProvider>
                  </PaywallProvider>
                </UserAuthProvider>
              </AuthProvider>
            </ChannelProvider>
          </BrowserRouter>
        </HelmetProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
